import { cn } from '../../lib/utils';

/** Review drawer status tones — DS tinted pill + dot (mirrors StatusPill). */
export type ReviewStatusTone = 'default' | 'pending' | 'approved' | 'rejected' | 'revision';

const TONE: Record<ReviewStatusTone, { pill: string; dot: string }> = {
  approved: { pill: 'border-emerald-200 bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  rejected: { pill: 'border-red-200 bg-red-50 text-red-700', dot: 'bg-red-500' },
  pending: { pill: 'border-amber-200 bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  revision: { pill: 'border-amber-200 bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  default: { pill: 'border-sky-200 bg-sky-50 text-sky-700', dot: 'bg-sky-500' },
};

/** Derive a tone from a humanized status label (for surfaces that only carry a label). */
export function reviewStatusTone(label?: string): ReviewStatusTone {
  const s = (label ?? '').toLowerCase();
  if (/live|approv|accept|complete|final/.test(s)) return 'approved';
  if (/reject|fail|declin/.test(s)) return 'rejected';
  if (/revision|changes/.test(s)) return 'revision';
  if (/pending|review|progress|submit/.test(s)) return 'pending';
  return 'default';
}

/** Status pill used in the Script / Content review drawer headers. */
export function ReviewStatusPill({ label, tone = 'default' }: { label: string; tone?: ReviewStatusTone }) {
  const t = TONE[tone];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize',
        t.pill,
      )}
    >
      <span className={cn('size-1.5 rounded-full', t.dot)} />
      {label}
    </span>
  );
}
